import { Checkbox, Input, Label, LabeledControl, Select } from '@zillow/constellation-10';
import { useLocale, useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import type { Fee, SelectedFeeOption, SelectedFeeOptions } from '@/src/types/costAndFees.type';
import { useListingLabel } from '@/src/lib/i18n/useListingLabel';
import { formatAmount } from '../costFeeUtils';
import {
  calculateFeeSectionTotal,
  type FeeFrequency,
  getAppliedFeeAmount,
  getFeeCounterValue,
  getFeeSelection,
  getFeeSelectionKey,
} from '../feeCalculation';
import {
  feeAdjustmentControls,
  feeContent,
  feeGroupLabel,
  feeItem,
  feeLabel,
  feeRow,
  feeSection,
  feeSectionTitle,
  feeTotalRow,
  feeValue,
  optionalFeeControl,
  optionalFeeRow,
} from './feeGroup.styles';

type StructuredFeeGroupProps = {
  id: string;
  title: string;
  totalLabel: string;
  required: Array<Fee>;
  optional: Array<Fee>;
  frequency: FeeFrequency;
  selectedFeeOptions: SelectedFeeOptions;
  onSelectedFeeOptionChange: (feeKey: string, selection: SelectedFeeOption | null) => void;
};

type FeeControlsProps = {
  fee: Fee;
  frequency: FeeFrequency;
  isOptional: boolean;
  selection: SelectedFeeOption | undefined;
  onSelectedFeeOptionChange: (feeKey: string, selection: SelectedFeeOption | null) => void;
};

function FeeControls({ fee, frequency, isOptional, selection, onSelectedFeeOptionChange }: FeeControlsProps) {
  const t = useTranslations('hdp');
  const label = useListingLabel();
  const feeName = label(fee.displayName || fee.type);
  const feeKey = getFeeSelectionKey(frequency, fee);
  const counterValue = getFeeCounterValue(fee, selection, isOptional);
  const minimum = isOptional ? Math.max(1, fee.counter.min) : fee.counter.min;
  const hasCounterControl = fee.counter.max === null || fee.counter.max > minimum;
  const hasOptionControl = fee.options.length > 0;
  const selectedOption = fee.options.some((option) => option.displayName === selection?.optionSelected)
    ? selection?.optionSelected
    : undefined;
  const [counterInputValue, setCounterInputValue] = useState(String(counterValue));

  useEffect(() => {
    setCounterInputValue(String(counterValue));
  }, [counterValue]);

  if (!hasCounterControl && !hasOptionControl) return null;

  const updateSelection = (next: Partial<SelectedFeeOption>) => {
    onSelectedFeeOptionChange(feeKey, {
      counterValue,
      optionSelected: selectedOption,
      ...next,
    });
  };

  return (
    <div className={feeAdjustmentControls}>
      {hasCounterControl && (
        <Input
          type="number"
          size="sm"
          fluid={false}
          min={minimum}
          max={fee.counter.max ?? undefined}
          value={counterInputValue}
          aria-label={t('feeQuantity', { name: feeName })}
          onChange={(event) => {
            const nextValue = event.target.value;
            setCounterInputValue(nextValue);
            if (nextValue === '') return;
            const value = Number(nextValue);
            if (Number.isFinite(value)) updateSelection({ counterValue: value });
          }}
          onBlur={() => {
            if (counterInputValue === '') setCounterInputValue(String(counterValue));
          }}
        />
      )}
      {hasOptionControl && (
        <Select
          size="sm"
          fluid={false}
          value={selectedOption ?? ''}
          aria-label={t('feeOption', { name: feeName })}
          onChange={(event) => updateSelection({ optionSelected: event.target.value || undefined })}
        >
          <option value="">{t('feeChooseOption')}</option>
          {fee.options.map((option) => (
            <option key={option.displayName} value={option.displayName}>
              {label(option.displayName)}
            </option>
          ))}
        </Select>
      )}
    </div>
  );
}

export function StructuredFeeGroup({
  id,
  title,
  totalLabel,
  required,
  optional,
  frequency,
  selectedFeeOptions,
  onSelectedFeeOptionChange,
}: StructuredFeeGroupProps) {
  const t = useTranslations('hdp');
  const label = useListingLabel();
  const locale = useLocale();
  const numberLocale = locale === 'es' ? 'es' : 'en-US';
  const total = calculateFeeSectionTotal(required, optional, frequency, selectedFeeOptions);

  return (
    <section className={feeSection} aria-labelledby={`${id}-fees-title`}>
      <h3 id={`${id}-fees-title`} className={feeSectionTitle}>
        {title}
      </h3>
      <div className={feeContent}>
        {required.length > 0 && (
          <>
            <span className={feeGroupLabel}>{t('feeRequired')}</span>
            {required.map((fee) => {
              const feeKey = getFeeSelectionKey(frequency, fee);
              const selection = getFeeSelection(frequency, fee, selectedFeeOptions);
              return (
                <div key={feeKey} className={feeItem}>
                  <div className={feeRow}>
                    <span className={feeLabel}>{label(fee.displayName)}</span>
                    <span className={feeValue}>{formatAmount(getAppliedFeeAmount(fee, selection, false))}</span>
                  </div>
                  <FeeControls
                    fee={fee}
                    frequency={frequency}
                    isOptional={false}
                    selection={selection}
                    onSelectedFeeOptionChange={onSelectedFeeOptionChange}
                  />
                </div>
              );
            })}
          </>
        )}

        {optional.length > 0 && (
          <>
            <span className={feeGroupLabel}>{t('feeOptional')}</span>
            {optional.map((fee, index) => {
              const feeKey = getFeeSelectionKey(frequency, fee);
              const selection = getFeeSelection(frequency, fee, selectedFeeOptions);
              const checked = Boolean(selection);
              const feeName = label(fee.displayName || fee.type);
              return (
                <div key={feeKey} className={feeItem}>
                  <div className={optionalFeeRow}>
                    <LabeledControl
                      className={optionalFeeControl}
                      controlId={`${id}-optional-fee-${index}`}
                      label={<Label>{feeName}</Label>}
                      control={
                        <Checkbox
                          checked={checked}
                          onChange={() =>
                            onSelectedFeeOptionChange(
                              feeKey,
                              checked
                                ? null
                                : {
                                    counterValue: Math.max(1, fee.counter.min),
                                    optionSelected: fee.options[0]?.displayName,
                                  },
                            )
                          }
                        />
                      }
                    />
                    <span className={feeValue}>
                      {checked ? formatAmount(getAppliedFeeAmount(fee, selection, true)) : '$0'}
                    </span>
                  </div>
                  {checked && (
                    <FeeControls
                      fee={fee}
                      frequency={frequency}
                      isOptional={true}
                      selection={selection}
                      onSelectedFeeOptionChange={onSelectedFeeOptionChange}
                    />
                  )}
                </div>
              );
            })}
          </>
        )}

        <div className={feeTotalRow}>
          <span>{totalLabel}</span>
          <span aria-live="polite" aria-atomic="true">
            {formatAmount(total, numberLocale)}
          </span>
        </div>
      </div>
    </section>
  );
}
